import React, { useState, useEffect } from "react";

import {
  Box,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import AttachFileIcon from "@material-ui/icons/AttachFile";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import {
  isMediaHeaderType,
  acceptForHeaderType,
} from "../../helpers/metaTemplateHeader";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#fffbeb",
    borderLeft: "4px solid #f59e0b",
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  field: { width: "100%", background: "#fff" },
  preview: {
    background: "#fff",
    border: "1px solid #fde68a",
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 13,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: "#374151",
  },
  previewLabel: { fontSize: 11, color: "#92400e", fontWeight: 600 },
  row: { display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" },
  attachRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  attachName: { color: "#92400e", wordBreak: "break-all", flex: 1, minWidth: 0 },
  sentRoot: {
    background: "#f0fdf4",
    borderLeft: "4px solid #22c55e",
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  sentText: { display: "flex", alignItems: "center", gap: 6, color: "#166534", fontWeight: 600, fontSize: 13 },
}));

// Sustituye {{n}} por los valores; deja {{n}} si está vacío (para el preview).
const renderBody = (body, vars) => {
  let out = body || "";
  vars.forEach((v, i) => {
    out = out.replace(
      new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, "g"),
      v && v.trim() ? v : `{{${i + 1}}}`
    );
  });
  return out;
};

// Mini-selector inline de plantillas APPROVED para enviar cuando la ventana Meta
// está cerrada. NO es el drawer de administración. Autocompleta {{1}}=contacto,
// {{2}}+=empresa, todo editable, con preview. Envía vía sendTemplate (feat_118).
const InlineTemplateSender = ({ ticket, contact, companyName, onSent, onCreate }) => {
  const classes = useStyles();
  const whatsappId = ticket?.whatsappId;
  const [templates, setTemplates] = useState(null); // null = cargando
  const [selectedId, setSelectedId] = useState("");
  const [vars, setVars] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // Adjunto real del envío (feat_308) — obligatorio si la plantilla lleva header media.
  const [mediaFile, setMediaFile] = useState(null);

  useEffect(() => {
    if (!whatsappId) return;
    let active = true;
    api
      .get(`/meta-templates/${whatsappId}`, { params: { status: "APPROVED" } })
      .then((r) => {
        if (!active) return;
        // hello_world (plantilla de muestra de Meta) no cuenta como plantilla usable.
        setTemplates((r.data || []).filter((t) => t.name !== "hello_world"));
      })
      .catch(() => active && setTemplates([]));
    return () => {
      active = false;
    };
  }, [whatsappId]);

  const selected = templates?.find((t) => String(t.id) === String(selectedId));

  const needsMedia = isMediaHeaderType(selected?.headerType);

  const handleSelect = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    setMediaFile(null);
    const t = templates.find((x) => String(x.id) === String(id));
    if (!t) {
      setVars([]);
      return;
    }
    const n = t.variableCount || 0;
    // {{1}} → nombre del contacto · {{2}}+ → empresa (editable).
    setVars(
      Array.from({ length: n }, (_, i) =>
        i === 0 ? contact?.name || "" : companyName || ""
      )
    );
  };

  const handleVarChange = (idx) => (e) => {
    const val = e.target.value;
    setVars((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  const handleSend = async () => {
    if (!selected || !contact?.number) return;
    if (needsMedia && !mediaFile) return;
    setSending(true);
    try {
      // multipart: el adjunto nunca viaja en JSON.
      const data = new FormData();
      data.append("toNumber", contact.number);
      data.append("variables", JSON.stringify(vars));
      if (needsMedia && mediaFile) data.append("media", mediaFile);

      await api.post(`/meta-templates/${whatsappId}/${selected.id}/send`, data);
      toast.success(i18n.t("metaTemplates.sendSuccess"));
      setMediaFile(null);
      // Colapsar el selector: la ventana 24hs sigue legítimamente cerrada (enviar
      // plantilla NO genera inbound del cliente), así que no reabrimos — solo
      // mostramos la vista compacta para no tapar el chat.
      setSent(true);
      onSent?.();
    } catch (err) {
      toastError(err);
    }
    setSending(false);
  };

  if (sent) {
    return (
      <Box className={classes.sentRoot}>
        <span className={classes.sentText}>
          <CheckCircleIcon style={{ fontSize: 18, color: "#22c55e" }} />
          {i18n.t("metaTemplates.inline.sentTitle")}
        </span>
        <Button size="small" onClick={() => setSent(false)} style={{ color: "#166534" }}>
          {i18n.t("metaTemplates.inline.sendAnother")}
        </Button>
      </Box>
    );
  }

  if (templates === null) {
    return (
      <Box className={classes.root}>
        <CircularProgress size={18} />
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Box className={classes.root}>
        <Typography variant="caption" style={{ color: "#92400e" }}>
          {i18n.t("metaTemplates.inline.noApproved")}
        </Typography>
        <div className={classes.row}>
          <Button
            size="small"
            variant="outlined"
            style={{ borderColor: "#f59e0b", color: "#92400e" }}
            onClick={onCreate}
          >
            {i18n.t("metaTemplates.inline.createButton")}
          </Button>
        </div>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Typography variant="caption" style={{ color: "#92400e", fontWeight: 600 }}>
        {i18n.t("metaTemplates.windowBanner.message")}
      </Typography>

      <FormControl size="small" variant="outlined" className={classes.field}>
        <InputLabel>{i18n.t("metaTemplates.inline.selectLabel")}</InputLabel>
        <Select
          value={selectedId}
          onChange={handleSelect}
          label={i18n.t("metaTemplates.inline.selectLabel")}
        >
          {templates.map((t) => (
            <MenuItem key={t.id} value={String(t.id)}>
              {t.name} ({t.language})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selected &&
        vars.map((v, idx) => (
          <TextField
            key={idx}
            size="small"
            variant="outlined"
            className={classes.field}
            label={i18n.t("metaTemplates.sendForm.variables", { index: idx + 1 })}
            value={v}
            onChange={handleVarChange(idx)}
          />
        ))}

      {needsMedia && (
        <div className={classes.attachRow}>
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={<AttachFileIcon />}
            style={{ borderColor: "#f59e0b", color: "#92400e", background: "#fff" }}
          >
            {i18n.t("metaTemplates.sendForm.attachmentChoose")}
            <input
              type="file"
              hidden
              accept={acceptForHeaderType(selected.headerType)}
              onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Typography variant="caption" className={classes.attachName}>
            {mediaFile
              ? `📎 ${mediaFile.name}`
              : i18n.t(`metaTemplates.sendForm.attachmentHint.${selected.headerType}`)}
          </Typography>
        </div>
      )}

      {selected && (
        <>
          <span className={classes.previewLabel}>
            {i18n.t("metaTemplates.inline.previewLabel")}
          </span>
          <div className={classes.preview}>{renderBody(selected.bodyText, vars)}</div>
        </>
      )}

      <div className={classes.row}>
        <Button size="small" onClick={onCreate} style={{ color: "#92400e" }}>
          {i18n.t("metaTemplates.inline.createButton")}
        </Button>
        <Button
          size="small"
          variant="contained"
          style={{ background: "#f59e0b", color: "#fff" }}
          onClick={handleSend}
          disabled={!selected || sending || !contact?.number || (needsMedia && !mediaFile)}
        >
          {sending ? (
            <CircularProgress size={16} style={{ color: "#fff" }} />
          ) : (
            i18n.t("metaTemplates.inline.sendButton")
          )}
        </Button>
      </div>
    </Box>
  );
};

export default InlineTemplateSender;
